import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Briefcase } from 'lucide-react';
import { api } from '../api';
import { Button, Modal, Input, Label, Select, Badge, useAsync } from '../components/ui';

function QuickMatterForm({ prefillClientId, onSubmit, onCancel }) {
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [f, setF] = useState({
    title: '', description: '', context: '',
    clientId: prefillClientId || '',
    researcherId: '', preparerId: '', verifierId: '',
  });
  useEffect(() => {
    Promise.all([
      api.get('/clients'),
      api.get('/employees', { params: { assignable: true } }),
    ]).then(([c, a]) => {
      setClients(c.data.items);
      const matterAgents = a.data.items.filter((e) => e.agentDomain !== 'business' && e.entityKind === 'agent');
      setAgents(matterAgents);
      // Pre-fill defaults from role
      const byRole = (role) => matterAgents.find((x) => x.role === role)?.id || '';
      setF((s) => ({
        ...s,
        researcherId: byRole('Legal Intelligence Lead'),
        preparerId: byRole('Client Delivery Owner'),
        verifierId: byRole('QA Lead'),
      }));
    });
  }, []);
  const { busy, err, run } = useAsync();
  const agentOpts = agents.map((x) => [x.id, `${x.fullName} · ${x.designation}`]);

  return (
    <form onSubmit={(e) => { e.preventDefault(); run(() => onSubmit(f)); }} className="space-y-4">
      <div>
        <Label>Title</Label>
        <Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
               placeholder="e.g. FSSAI license renewal for Acme Foods" data-testid="qm-title" />
      </div>
      <div>
        <Label>Client (company)</Label>
        <Select required value={f.clientId} onChange={(e) => setF({ ...f, clientId: e.target.value })}
                options={[['', '— pick a company —'], ...clients.map((c) => [c.id, c.clientName])]} data-testid="qm-client" />
      </div>
      <div>
        <Label>Description</Label>
        <textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                  placeholder="One-line summary of the work"
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 resize-none"
                  data-testid="qm-desc" />
      </div>
      <div>
        <Label>Context (what does the client want?)</Label>
        <textarea rows={4} value={f.context} onChange={(e) => setF({ ...f, context: e.target.value })}
                  placeholder="The client's objective, constraints, urgency, expected outcome. Agents will read this before answering."
                  className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40 resize-none"
                  data-testid="qm-ctx" />
      </div>
      <div className="border-t border-white/[0.06] pt-3">
        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-200/70 mb-3">3-agent workflow</div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label>Researcher (owns intake + research)</Label>
            <Select value={f.researcherId} onChange={(e) => setF({ ...f, researcherId: e.target.value })} options={agentOpts} data-testid="qm-r" />
          </div>
          <div>
            <Label>Preparer (drafts the output)</Label>
            <Select value={f.preparerId} onChange={(e) => setF({ ...f, preparerId: e.target.value })} options={agentOpts} data-testid="qm-p" />
          </div>
          <div>
            <Label>Verifier (final QA sign-off)</Label>
            <Select value={f.verifierId} onChange={(e) => setF({ ...f, verifierId: e.target.value })} options={agentOpts} data-testid="qm-v" />
          </div>
        </div>
      </div>
      {err && <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">{err}</div>}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={busy} data-testid="qm-save">{busy ? 'Creating…' : 'Create matter'}</Button>
      </div>
    </form>
  );
}

export default function Matters() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const prefillClient = sp.get('client') || '';
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(!!prefillClient);
  const [query, setQuery] = useState('');

  const load = () => Promise.all([api.get('/matters'), api.get('/clients')])
    .then(([m, c]) => { setRows(m.data.items); setClients(c.data.items); });
  useEffect(() => { load(); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.clientName || '—';
  const filtered = rows.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (r.title + ' ' + (r.matterCode || '') + ' ' + clientName(r.clientId)).toLowerCase().includes(q);
  });

  return (
    <div data-testid="liq-matters">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.32em] text-amber-200/60 mb-2">Work</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-[32px] leading-tight text-slate-50">Matters<span className="text-amber-300">.</span></h1>
            <p className="text-slate-400 text-[13px] mt-2 max-w-2xl">Every matter is auto-assigned to a Researcher, Preparer and Verifier. Click a matter to open its group chat, add context and upload reference documents.</p>
          </div>
          <button onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-gradient-to-b from-amber-400 to-amber-500 text-amber-950 font-semibold text-[13px] px-5 py-2.5 rounded-full hover:brightness-110 hover:scale-[1.02] shadow-lg"
            data-testid="m-new"><Plus size={14} strokeWidth={2.5} /> New matter</button>
        </div>
      </div>

      <div className="mb-3">
        <input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search matters…"
          className="w-full max-w-md bg-white/[0.03] border border-white/[0.06] rounded-full px-4 py-2 text-[12px] text-slate-100 placeholder-slate-500 outline-none focus:border-amber-500/40"
          data-testid="m-search" />
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
        <div className="grid grid-cols-[120px_1fr_1fr_140px_120px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/[0.06]">
          <div>Code</div><div>Title</div><div>Company</div><div>Stage</div><div>Docs</div>
        </div>
        {filtered.length === 0 ? (
          <div className="px-5 py-16 text-slate-600 text-sm text-center">
            {rows.length === 0 ? 'No matters yet. Click + New matter.' : 'No matches.'}
          </div>
        ) : filtered.map((m) => (
          <div key={m.id} onClick={() => nav(`/liq/matters/${m.id}`)}
            className="grid grid-cols-[120px_1fr_1fr_140px_120px] px-5 py-3.5 items-center hover:bg-white/[0.02] cursor-pointer border-b border-white/[0.04] last:border-0 group">
            <div className="font-mono text-[11px] text-amber-200/80">{m.matterCode}</div>
            <div className="flex items-center gap-2 min-w-0">
              <Briefcase size={13} className="text-emerald-300/70 shrink-0" />
              <span className="text-slate-100 truncate group-hover:text-amber-100">{m.title}</span>
            </div>
            <div className="text-slate-400 text-[12px] truncate">{clientName(m.clientId)}</div>
            <div><Badge tone="sky">{m.stage}</Badge></div>
            <div className="text-slate-400 text-[12px]">{(m.documents || []).length}</div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New matter" wide>
        <QuickMatterForm prefillClientId={prefillClient}
          onSubmit={async (f) => { const r = await api.post('/matters-quick', f); setOpen(false); nav(`/liq/matters/${r.data.id}`); }}
          onCancel={() => setOpen(false)} />
      </Modal>
    </div>
  );
}
